/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useMemo} from 'react';

import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {
  Box, createStylesContext, Flex, SimpleGrid, Spinner, useMultiStyleConfig, VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Markup} from '@amblique/base/app/components/shared/markup';
import {ContentAccordion} from '@amblique/king-living/app/components/footer/content-accordion';
import {ShowroomFooterBanner} from '@amblique/king-living/app/components/showroom';
import {useFooterBySiteId} from '@amblique/king-living/app/contentful/hooks/use-footer';
import {useIsLargeScreen} from '@amblique/king-living/app/hooks/use-memoized-breakpoint';
import {contentfulTextToMarkupSource} from '@amblique/king-living/app/utils/contentful-markup-source';

const [StylesProvider] = createStylesContext('Footer');

/**
 * Transform Contentful footer columns to the format expected by ContentAccordion
 * @param {Array} columns - Array of FooterColumn items from Contentful
 * @returns {Array} - Array of content objects for ContentAccordion
 */
const transformColumnToAccordionContent = (column) => {
  if (!column) return null;

  // Build HTML links from linksCollection
  const links = (column.linksCollection?.items || []).filter(Boolean);
  const linksHtml = links
    .filter((link) => link?.linkText || link?.text) // Only require label, url can be null
    .map((link) => {
      const target = link.openInNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';
      // Use '#' as fallback if url is null
      const href = link.url || '#';
      const label = link.linkText ?? link.text;

      return `<a href="${href}"${target}>${label}</a>`;
    })
    .join('\n');

  return {
    name: column.title || '',
    body: linksHtml,
  };
};

const Footer = ({...otherProps}) => {
  const styles = useMultiStyleConfig('Footer');
  const isMobile = !useIsLargeScreen();
  const {site} = useMultiSite();

  // Fetch footer data from Contentful using siteId
  // Using 'withColumns' variant - custom query with explicit limits to avoid Contentful complexity errors
  const {data: footerData, isLoading: isFooterLoading} = useFooterBySiteId(site?.id, {
    variant: 'withColumns',
  });

  // Footer link columns in Contentful order; layout uses CSS grid (row-major: left→right, top→bottom)
  const footerLinkColumns = useMemo(() => {
    const items = footerData?.columnsCollection?.items;

    if (!items?.length) return [];

    return items
      .filter(Boolean)
      .map((column, index) => {
        const content = transformColumnToAccordionContent(column);

        if (!content) return null;

        return {
          content,
          key: column?.sys?.id ?? `footer-column-${index}`,
        };
      })
      .filter(Boolean);
  }, [footerData]);

  const footerLinksDesktopColumns = Math.min(footerLinkColumns.length, 3) || 1;
  const footerLinkColumnContents = useMemo(
    () => footerLinkColumns.map((entry) => entry.content),
    [footerLinkColumns]
  );

  // Get logo from Contentful or use default
  // Image content type has: desktopAsset.url, mobileAsset.url, tabletAsset.url for image sources
  // The 'url' field on Image is a link URL (not the image source)
  const logoUrl = useMemo(() => {
    // Try desktop asset first, then mobile, then fallback to local
    if (footerData?.logo?.desktopAsset?.url) {
      return footerData.logo.desktopAsset.url;
    }
    if (footerData?.logo?.mobileAsset?.url) {
      return footerData.logo.mobileAsset.url;
    }

    return getAssetUrl('static/img/king-logo.svg');
  }, [footerData]);

  // Get countries from Contentful or use default
  const countries = useMemo(() => {
    const items = (footerData?.countriesCollection?.items || []).filter(Boolean);

    if (items.length > 0) {
      return items;
    }

    // Fallback to default countries
    return null;
  }, [footerData]);

  // Get payment icons from Contentful IconSet or use default
  const paymentIcons = useMemo(() => {
    // The payments field links to IconSet, which contains iconsCollection
    const iconSets = footerData?.paymentsCollection?.items;

    if (iconSets?.length > 0) {
      // Get icons from the first IconSet (or combine all if multiple)
      const allIcons = iconSets
        .filter(Boolean)
        .flatMap((iconSet) => (iconSet?.iconsCollection?.items || []).filter(Boolean));

      if (allIcons.length > 0) {
        return allIcons;
      }
    }

    // Fallback to default payment icons
    return null;
  }, [footerData]);

  // Get social links from Contentful (passed to Showroom/Newsletter)
  const socialLinks = useMemo(() => {
    return (footerData?.socialLinksCollection?.items || []).filter(Boolean);
  }, [footerData]);

  // KL-1074 — per-region background image for the "Visit your nearest showroom"
  // section, configured on the Footer content model. Takes precedence over the
  // geolocation-based nearest-showroom featured image (fallback handled inside
  // the banner).
  const showroomFooterImage = footerData?.showroomFooter || null;

  return (
    <Box as="footer" {...styles.container} {...otherProps}>
      {/* Showroom Section - Full Width Above Footer */}
      <ShowroomFooterBanner configuredImage={showroomFooterImage} socialLinks={socialLinks} />
      <VStack {...styles.content} as="section">
        <StylesProvider value={styles}>
          {countries && countries.length > 0 && (
            <Box {...styles.topFooter}>
              {countries.map((country, index) => (
                <Box key={country.sys?.id || index}>
                  {country.url ? (
                    <a
                      href={country.url}
                      rel={country.openInNewTab ? 'noopener noreferrer' : undefined}
                      target={country.openInNewTab ? '_blank' : undefined}
                    >
                      {country.text}
                      {country.totalCount != null && (
                        <sup style={{fontSize: '0.7em', marginLeft: '2px'}}>
                          {country.totalCount}
                        </sup>
                      )}
                    </a>
                  ) : (
                    <>
                      {country.text}
                      {country.totalCount != null && (
                        <sup style={{fontSize: '0.7em', marginLeft: '2px'}}>
                          {country.totalCount}
                        </sup>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </Box>
          )}
          <Flex {...styles.mainContainer}>
            <Flex {...styles.siteInformation}>
              <Box {...styles.brandSection}>
                <Box className="footer-brand">
                  <img alt="King Living" className="footer-logo" loading="lazy" src={logoUrl} />
                  <Box className="footer-info">
                    {footerData?.aboutUs ? (
                      <Markup source={contentfulTextToMarkupSource(footerData.aboutUs)} />
                    ) : null}
                  </Box>
                </Box>
              </Box>
            </Flex>

            {isFooterLoading ? (
              <Box display="flex" justifyContent="center" p={8} width="100%">
                <Spinner size="lg" />
              </Box>
            ) : isMobile ? (
              <Box className="accordion-section" {...styles.accordionSection}>
                <ContentAccordion content={footerLinkColumnContents} />
              </Box>
            ) : (
              <SimpleGrid
                {...styles.linksSection}
                className="footer-links-section"
                columns={footerLinksDesktopColumns}
              >
                {footerLinkColumns.map(({content, key}) => (
                  <ContentAccordion key={key} content={[content]} />
                ))}
              </SimpleGrid>
            )}
          </Flex>

          <VStack {...styles.bottomFooterWrapper}>
            <Box {...styles.bottomCopyrightWrapper}>
              <Box {...styles.bottomCopyright}>
                {footerData?.legalText ? (
                  <Markup source={contentfulTextToMarkupSource(footerData.legalText)} />
                ) : null}
              </Box>
              {paymentIcons && paymentIcons.length > 0 && (
                <Box {...styles.bottomIcons}>
                  {paymentIcons.map((icon, index) => {
                    const iconTitle = icon?.title || icon?.name || `Payment ${index + 1}`;

                    // Prefer SVG code if available, otherwise use image URL
                    if (icon?.svgCode) {
                      return (
                        <Box
                          key={icon.sys?.id || index}
                          aria-label={iconTitle}
                          dangerouslySetInnerHTML={{__html: icon.svgCode}}
                          title={iconTitle}
                        />
                      );
                    }

                    if (icon?.iconImage?.url) {
                      return (
                        <img
                          key={icon.sys?.id || index}
                          alt={iconTitle}
                          loading="lazy"
                          src={icon.iconImage.url}
                        />
                      );
                    }

                    return null;
                  })}
                </Box>
              )}
            </Box>
            <Box {...styles.bottomFooter}>
              {footerData?.siteNotes ? (
                <Markup source={contentfulTextToMarkupSource(footerData.siteNotes)} />
              ) : null}
            </Box>
          </VStack>
        </StylesProvider>
      </VStack>
    </Box>
  );
};

export default Footer;
